import { parsePhoneNumberFromString } from 'libphonenumber-js'
import type { PartnerDriverDetails, UpdatePartnerDriverBody } from 'src/interfaces/partnerDriver'
import {
  centsToDollarsInput,
  dollarsInputToCents,
  percentageToInput,
  toPercentageNumber,
} from 'src/utils/partnerDriver/money'
import type { PartnerDriverProfileFormValues } from './PartnerDriverProfileForm.interface'

const TEXT_FIELDS = ['firstName', 'lastName', 'email'] as const

// The backend rejects a phone that already exists without excluding the driver itself, so the
// same number in another format ("(415) 555-0132" vs "+14155550132") must not count as a change.
const normalizePhone = (value: string): string => {
  const trimmedValue = value.trim()

  return parsePhoneNumberFromString(trimmedValue, 'US')?.number ?? trimmedValue
}

export const toProfileFormValues = (
  driver: PartnerDriverDetails,
): PartnerDriverProfileFormValues => {
  return {
    firstName: driver.firstName ?? '',
    lastName: driver.lastName ?? '',
    email: driver.email ?? '',
    phone: driver.phone ?? '',
    reimbursementAmount: centsToDollarsInput(driver.reimbursementAmount),
    reimbursementPercentage: percentageToInput(driver.reimbursementPercentage),
  }
}

/**
 * Only changed fields. The PUT has no whitelist, and an unchanged-but-sent email still runs the
 * backend's email-change path (pending-Yardstik check, report deletion, comms sync).
 */
export const toUpdatePartnerDriverBody = (
  initial: PartnerDriverProfileFormValues,
  values: PartnerDriverProfileFormValues,
): UpdatePartnerDriverBody => {
  const body: UpdatePartnerDriverBody = {}

  TEXT_FIELDS.forEach((field) => {
    const nextValue = values[field].trim()

    if (nextValue !== initial[field].trim()) {
      body[field] = nextValue
    }
  })

  const nextPhone = normalizePhone(values.phone)

  if (nextPhone !== normalizePhone(initial.phone)) {
    body.phone = nextPhone
  }

  const nextAmount = dollarsInputToCents(values.reimbursementAmount)

  if (nextAmount !== dollarsInputToCents(initial.reimbursementAmount)) {
    body.reimbursementAmount = nextAmount
  }

  const nextPercentage = toPercentageNumber(values.reimbursementPercentage.trim()) ?? 0

  if (nextPercentage !== (toPercentageNumber(initial.reimbursementPercentage.trim()) ?? 0)) {
    body.reimbursementPercentage = nextPercentage
  }

  return body
}
